import React, { Ref } from 'react';
import { XIcon, CheckIcon } from 'lucide-react';
import { CTAButton } from '../CTAButton';
import { MOCKUP_URL } from '../../data/content';
type HeroProps = {
  ctaRef: Ref<HTMLAnchorElement>;
};
const noMore = ['No more relying solely on software simulations.', 'No more confusing crown movement with actual root control.', 'No more approaching challenging torque movements without a structured clinical decision process.'];
const trust = ['Instant Digital Access', 'One-Time Payment', 'Lifetime Access', '6 Clinical Volumes + 5 Exclusive Bonuses', 'Practical Clinical Decision Tools'];
export function Hero({
  ctaRef
}: HeroProps) {
  return <header className="bg-ink text-white" data-id="element-202" data-mp="voouogmid9jf">
      <div className="border-b border-white/10" data-id="element-203" data-mp="10kdtghqpemeo">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5" data-id="element-204" data-mp="10n9re62jxb34">
          <span className="font-display text-base font-extrabold tracking-tight" data-id="element-205" data-mp="15iff9afikztu">Dr. Valeria Montes</span>
          <span className="text-xs font-semibold text-electric-light" data-id="element-206" data-mp="1ah8rkzj2eksf">Orthodontic Biomechanics</span>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-7 px-4 pb-16 pt-8 lg:grid-cols-2 lg:gap-x-14 lg:pb-24 lg:pt-16" data-id="element-207" data-mp="4o6mpojyd38k">
        <div className="lg:col-start-1 lg:row-start-1" data-id="element-208" data-mp="3vu95tedzg8hu">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-electric sm:text-sm" data-id="element-209" data-mp="3vwu7a2adcmf2">
            The clinical blueprint for root movement & torque control
          </p>
          <h1 className="text-balance mt-4 font-display text-[42px] font-black leading-[1] tracking-tight sm:text-6xl lg:text-[68px]" data-id="element-210" data-mp="sdiqua0fal40">
            Stop Guessing Your <span className="text-electric" data-id="element-211" data-mp="inpirizhoo4i">Torque Mechanics.</span>
          </h1>
          <p className="mt-5 text-lg font-semibold leading-snug text-white sm:text-2xl" data-id="element-212" data-mp="en5jo9vcvafq">
            Build a clear, biomechanically sound torque-control plan for your next orthodontic case in just{' '}
            <span className="bg-electric/20 px-1 text-electric-light" data-id="element-213" data-mp="16ebmwz6dho98">15 minutes.</span>
          </p>
        </div>

        <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center" data-id="element-214" data-mp="30lnoeolvy7s2">
          <div className="rounded-3xl bg-[#EAF3FF] p-3 sm:p-5" data-id="element-215" data-mp="35ljssoyhw185">
            <img src={MOCKUP_URL} alt="Orthodontic Torque Control Blueprint: 6 clinical volumes, digital framework and 5 bonuses" className="h-auto w-full" data-id="element-216" data-mp="35n3c6ofg6se5" />
          </div>
        </div>

        <div className="lg:col-start-1 lg:row-start-2" data-id="element-217" data-mp="2hv69pnxw1cpp">
          <p className="font-display text-sm font-extrabold uppercase tracking-wide text-electric-light" data-id="element-218" data-mp="2hxiltkgyssuu">
            Orthodontic Torque Control Blueprint™
          </p>
          <p className="mt-3 text-base leading-relaxed text-white/80 sm:text-lg" data-id="element-219" data-mp="27yjmisi3a9ki">
            Discover the <strong className="text-white" data-id="element-220" data-mp="2cx4fq7yxbszg">15-Minute Torque Planning Framework™</strong> — a practical,
            visual system designed to help you evaluate root movement, identify unwanted tipping risks, and organize
            more precise orthodontic treatment plans.
          </p>

          <ul className="mt-6 space-y-3" data-id="element-221" data-mp="1vo24i5g2utjc">
            {noMore.map(item => <li key={item} className="flex gap-3 text-[15px] leading-snug text-white/90" data-id="element-222" data-mp="1qofr9w091daj">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-danger/20" data-id="element-223" data-mp="25dby5pqkkk9k">
                  <XIcon className="h-3.5 w-3.5 text-danger" aria-hidden="true" data-id="element-224" data-mp="20l2dao4baae5" />
                </span>
                {item}
              </li>)}
          </ul>

          <p className="mt-6 text-base leading-relaxed text-white/80" data-id="element-225" data-mp="2ac6p5eyoxf4r">
            With this 6-volume clinical library and 5 exclusive bonuses, you’ll learn how to evaluate torque
            requirements, understand biomechanical limitations, and make more informed planning decisions.
          </p>
          <p className="mt-4 font-display text-lg font-bold text-white" data-id="element-226" data-mp="txf0nd4o0s0q">
            From uncertain torque planning to a structured clinical strategy — starting with your very next case.
          </p>

          <div className="mt-7" data-id="element-227" data-mp="qg91k573drvd">
            <CTAButton ref={ctaRef} label="Get the Torque Control Blueprint now — $27" subLabel="Instant access · One-time payment" data-id="element-228" data-mp="qexyk7lh0o5b" />
          </div>

          <ul className="mt-5 grid grid-cols-1 gap-2 min-[400px]:grid-cols-2" data-id="element-229" data-mp="10dx77q68kjsb">
            {trust.map(t => <li key={t} className="flex items-start gap-2 text-sm font-medium text-white/75" data-id="element-230" data-mp="15djn67dg8e4t">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-cta" aria-hidden="true" data-id="element-231" data-mp="7prnn6qx50u2" />
                {t}
              </li>)}
          </ul>
        </div>
      </div>
    </header>;
}